import { MatrixEvent, RelationType } from 'matrix-js-sdk';

/**
 * Chat-Nachricht mit Array aus MatrixEvents.
 * Begründung: Keine AFO (Bestandteil der Redux-Architektur).
 */
export class ChatMessage {
  // messages:
  // Erster Eintrag ist die Original-Nachricht, auf die sich Edit- und Reaction-Events beziehen.
  // Alle weiteren Einträge sind deren Modifizierungen (relates-to).
  // reactions - key: Event-ID (zum Auffinden bei Redaction)
  // Alle Reactions beziehen sich auf die Original-Nachricht (Ursprungsnachricht).
  private constructor(
    private readonly _messages: MatrixEvent[],
    private readonly _reactions: MatrixEvent[],
    private readonly _redactReason: string
  ) {}

  get messages(): MatrixEvent[] {
    return this._messages;
  }

  get reactions(): MatrixEvent[] {
    return this._reactions;
  }

  /**
   * Durch Verwendung dieser Factory-Methode ist gewährleistet,
   * dass mindestens ein Event vorhanden ist,
   * sodass die Getter einen validen Rückgabewert liefern.
   */
  static new(newMessageEvent: MatrixEvent): ChatMessage {
    return new ChatMessage([newMessageEvent], [], '');
  }

  /**
   * Erstes geladenes Event im Zeitfenster. Nicht zwingend das Original —
   * wenn dieses noch nicht eingespeist wurde, ist first das älteste Edit-Event.
   */
  get first(): MatrixEvent {
    return this._messages[0];
  }

  hasOriginalEvent(): boolean {
    return this.first.getContent()['m.relates_to']?.rel_type !== RelationType.Replace;
  }

  get originalId(): string | undefined {
    return this.hasOriginalEvent() ? this.first.getId() : this.first.getContent()['m.relates_to']?.event_id;
  }

  get curr(): MatrixEvent {
    return this._messages[this._messages.length - 1];
  }

  isEdited(): boolean {
    return this._messages.length > 1;
  }

  isRedacted(): boolean {
    // Prüfe erst MatrixEvent und dann ChatMessage. Zum Zeitpunkt des Handlings
    // des Redact-Events ist die Information noch nicht im Message-Event.
    return this.first.isRedacted() || !!this._redactReason;
  }

  getRedactReason(): string {
    return this.first.isRedacted()
      ? this.first.getUnsigned().redacted_because?.content?.['reason']
      : this._redactReason;
  }

  /********************************************************************************************************************
   * Reducers
   ********************************************************************************************************************/

  /**
   * Use Case 1: ChatMessage wurde aufgrund eines EditEvents noch vor Eintreffen des Original-Message-Events erzeugt.
   * Dieses wird nun nachträglich eingespeist.
   * Use Case 2: Erweitere Messages um Edit-Message-Event.
   */
  reduceByMessageEvent(event: MatrixEvent): ChatMessage {
    if (this.isRedacted()) {
      // Beim Einspeisen der LiveTimeline-Events eines Raums Usus.
      return this;
    }

    if (this._messages.some(m => m.getId() === event.getId())) {
      // Doppeltes Event - bereits berücksichtigt
      return this;
    }

    const newMessages = this.insertSorted(this._messages, event);

    return new ChatMessage(newMessages, this._reactions, this._redactReason);
  }

  /**
   * Füge Reaction zu Reactions hinzu.
   */
  reduceByReactionEvent(event: MatrixEvent): ChatMessage {
    if (this.isRedacted()) {
      // Beim Einspeisen der LiveTimeline-Events eines Raums Usus.
      return this;
    }

    const txnId = event.getTxnId();

    if (txnId) {
      const existingReaction = this._reactions.find(r => r.getTxnId() === txnId);
      if (existingReaction) {
        const newReactions = this._reactions.map(r => (r.getTxnId() === txnId ? event : r));
        return new ChatMessage(this._messages, newReactions, this._redactReason);
      }
    }

    const newReactions = this.insertSorted(this._reactions, event);

    return new ChatMessage(this._messages, newReactions, this._redactReason);
  }

  /**
   * Markiere als gelöscht mit Reason aus Event.
   */
  reduceByRedactMessageEvent(event: MatrixEvent): ChatMessage {
    // Sicherstellen, dass Redact-Event zu einem State Change führt,
    // sodass Display-DTOs neu berechnet werden. Insbesondere auch dann,
    // wenn MatrixEvent der Nachricht bereits als redacted gekennzeichnet ist.

    // Text, der statt der gelöschten Nachricht angezeigt werden soll.
    const reason = (event.getContent()?.['reason'] as string) ?? 'leer';

    // Lösche Reactions, aber behalte Messages.
    // Zwar wird die Original-Nachricht von Matrix als redacted gekennzeichnet,
    // aber erst nach dem Handling des Redacted-Events (und damit zu spät).
    // Daher wird der Grund (gelöscht von ... am ...) aus dem RedactionEvent
    // entnommen und an der ChatMessage gespeichert.
    // Außerdem wird first.clearEvent in Kürze von Matrix gelöscht,
    // sodass insbesondere der entschlüsselte Originaltext verloren geht.

    const newMessages = this.insertSorted(this.messages, event);

    return new ChatMessage(newMessages, [], reason);
  }

  reduceByRedactReactionEvent(event: MatrixEvent): ChatMessage {
    if (this.isRedacted()) {
      // Beim Einspeisen der LiveTimeline-Events eines Raums Usus.
      return this;
    }

    const redactedId = event.event?.redacts;
    const newReactions = this._reactions.filter(r => r.getId() !== redactedId);

    return new ChatMessage(this._messages, newReactions, this._redactReason);
  }

  /**
   * Erzeugt eine neue ChatMessage-Instanz mit denselben (in-place mutierten) Events.
   * Wird nach MatrixEventEvent.Decrypted genutzt, um einen State-Change auszulösen.
   */
  cloneForDecryption(): ChatMessage {
    return new ChatMessage([...this._messages], this._reactions, this._redactReason);
  }

  hasReaction(reactionId: string): boolean {
    return this._reactions.some(r => r.getId() === reactionId);
  }

  /**
   * Effizientes Einsortieren ohne sort
   */
  private insertSorted(messages: MatrixEvent[], event: MatrixEvent): MatrixEvent[] {
    const ts = event.getTs();

    let left = 0;
    let right = messages.length;

    while (left < right) {
      const mid = (left + right) >> 1;
      if (messages[mid].getTs() <= ts) {
        left = mid + 1;
      } else {
        right = mid;
      }
    }

    return [...messages.slice(0, left), event, ...messages.slice(left)];
  }
}
